import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Radio,
  Button,
  Alert,
  message,
  InputNumber,
  Spin,
} from 'antd';
import {
  FileTextTwoTone,
  EnvironmentTwoTone,
  EditOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ticketService from '../services/ticketService';
import { directOrderService } from '../services/directOrderService';
import inventoryService from '../services/inventoryService';
import lineVotesService from '../services/lineVotesService';
import DOTheme from './directOrders/doTheme';
import { AMENDABLE_FIELDS, getApprovalStatus, ticketStatus } from '../utils/ticketFields';
import { API_ORIGIN } from '../config';
import { firstName, useApprovers } from '../utils/approverStore';
import '../styles/support.css';
import { LIMIT_LABEL, totalLimitRule } from '../utils/directOrderLimits';

const { TextArea } = Input;
const { Option } = Select;

const CreateTicketModal = ({ visible, onClose, userEmail, preselectedDoId = null }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { approvers } = useApprovers();
  const approverNames = approvers.map(firstName).filter(Boolean);
  const firstApprover = approverNames[0] || 'The first approver';
  const chainText = approverNames.length
    ? `${approverNames.slice(0, -1).join(', ')}${approverNames.length > 1 ? ' and ' : ''}${approverNames[approverNames.length - 1]} approve in turn.`
    : 'The three approvers approve in turn.';
  const [step, setStep] = useState('select'); // 'select' or 'form'
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [ticketType, setTicketType] = useState('amend');
  const [directOrders, setDirectOrders] = useState([]);
  const [selectedDO, setSelectedDO] = useState(null);
  const [loadingDOs, setLoadingDOs] = useState(false);
  const [selectedFields, setSelectedFields] = useState([]);
  const [locationForm] = Form.useForm();
  const [locationSubmitting, setLocationSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null); // { message, existingTicketId } | null

  // Your earlier direct order requests (the backend only returns your own)
  const [myTickets, setMyTickets] = useState([]);
  const [myTicketsLoading, setMyTicketsLoading] = useState(false);

  // Lookup options for the 'select'-type amendable fields — same sources as
  // DirectOrderEditModal.js, so a value amended here matches what the DO form
  // itself would accept (doInitiative/doJustification are FK ids, not text).
  const [initiatives, setInitiatives] = useState([]);
  const [justifications, setJustifications] = useState([]);
  const [lineVotes, setLineVotes] = useState([]);

  useEffect(() => {
    const fetchInitiatives = async () => {
      try {
        const response = await fetch(`${API_ORIGIN}/api/direct-orders/initiatives`, { credentials: 'include' });
        const data = await response.json();
        if (data.success && Array.isArray(data.initiatives)) {
          setInitiatives(data.initiatives.map(item => ({ value: String(item.id), label: item.initiative })));
        }
      } catch (error) { /* non-fatal — field just shows no options */ }
    };
    const fetchJustifications = async () => {
      try {
        const response = await fetch(`${API_ORIGIN}/api/direct-orders/justifications`, { credentials: 'include' });
        const data = await response.json();
        if (data.success && Array.isArray(data.justifications)) {
          setJustifications(data.justifications.map(item => ({ value: String(item.justificationId), label: item.justification })));
        }
      } catch (error) { /* non-fatal */ }
    };
    const fetchLineVotes = async () => {
      try {
        const response = await lineVotesService.getAll();
        if (response.success && Array.isArray(response.lineVotes)) {
          setLineVotes(response.lineVotes
            .filter(item => item && item.id != null)
            .map(item => ({ value: item.id, label: `${item.departmentCode ?? '—'} - ${item.LineVote} - ${item.Festival}` })));
        }
      } catch (error) { /* non-fatal */ }
    };
    fetchInitiatives();
    fetchJustifications();
    fetchLineVotes();
  }, []);

  const SELECT_OPTIONS_BY_FIELD = {
    doInitiative: initiatives,
    doJustification: justifications,
    lineVoteId: lineVotes,
  };

  useEffect(() => {
    if (visible) {
      if (preselectedDoId) {
        // If preselected, skip to form
        setStep('form');
        setSelectedCategory('directOrder');
        fetchDirectOrders();
        form.setFieldsValue({ doId: preselectedDoId });
        fetchDODetails(preselectedDoId);
      } else {
        // Start at selection screen
        setStep('select');
        setSelectedCategory(null);
      }
      fetchMyTickets();
    } else {
      // Reset everything on close
      setStep('select');
      setSelectedCategory(null);
      form.resetFields();
      setSelectedDO(null);
      setSelectedFields([]);
      setTicketType('amend');
      locationForm.resetFields();
      setSubmitError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, preselectedDoId]);

  const fetchMyTickets = async () => {
    if (!userEmail) return;
    setMyTicketsLoading(true);
    try {
      const response = await ticketService.listTickets({ email: userEmail });
      setMyTickets(response.success ? response.tickets || [] : []);
    } catch (error) {
      setMyTickets([]);
    } finally {
      setMyTicketsLoading(false);
    }
  };

  const fetchDirectOrders = async () => {
    setLoadingDOs(true);
    try {
      const response = await directOrderService.getEligibleDirectOrdersForTickets(userEmail);
      if (response && response.orders) {
        // These are already filtered for eligibility (all 3 approvals = 1, isDeleted = 0)
        setDirectOrders(response.orders);
      }
    } catch (error) {
      message.error('Failed to load eligible direct orders');
    } finally {
      setLoadingDOs(false);
    }
  };

  const handleDOChange = (doId) => {
    setSubmitError(null);
    fetchDODetails(doId);
  };

  const fetchDODetails = async (doId) => {
    setSelectedFields([]);
    form.setFieldsValue({ fieldsToAmend: [] });
    try {
      const response = await directOrderService.getDirectOrderById(doId);
      if (response) {
        // Handle both response formats: { success: true, order: {...} } or direct order object
        const order = response.order || response;
        setSelectedDO(order);
      }
    } catch (error) {
      const order = directOrders.find(o => o.doId === doId);
      setSelectedDO(order);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      setSubmitError(null);

      const ticketData = {
        doId: values.doId,
        ticket_type: ticketType,
        created_by_email: userEmail,
        reason: values.reason || ''
      };

      // For amend tickets, build proposed_changes from selected fields
      if (ticketType === 'amend') {
        if (!selectedFields || selectedFields.length === 0) {
          message.error('Please select at least one field to amend');
          setLoading(false);
          return;
        }

        const proposedChanges = {};
        selectedFields.forEach(fieldKey => {
          const value = values[`change_${fieldKey}`];
          if (value !== undefined && value !== null && value !== '') {
            proposedChanges[fieldKey] = value;
          }
        });

        if (Object.keys(proposedChanges).length === 0) {
          message.error('Please provide new values for the selected fields');
          setLoading(false);
          return;
        }

        ticketData.proposed_changes = proposedChanges;
      }

      const response = await ticketService.createTicket(ticketData);

      if (response.success) {
        message.success(response.message || 'Ticket created successfully!');
        form.resetFields();
        onClose();
      } else {
        // Detect the conflict by the presence of existingTicketId rather than
        // trusting an exact status code — the backend may not always send 409.
        setSubmitError({
          message: response.error || 'Failed to create ticket',
          existingTicketId: response.existingTicketId || null,
        });
      }
    } catch (error) {
      setSubmitError({ message: 'An error occurred while creating the ticket', existingTicketId: null });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDirectOrder = () => {
    setSelectedCategory('directOrder');
    setStep('form');
    fetchDirectOrders();
  };

  const handleSelectInventoryLocation = () => {
    setSelectedCategory('inventoryLocation');
    setStep('form');
  };

  const handleBack = () => {
    setStep('select');
    setSelectedCategory(null);
    form.resetFields();
    setSelectedDO(null);
    setSelectedFields([]);
    setTicketType('amend');
    locationForm.resetFields();
  };

  // The request types stay on screen; switching type starts that form afresh.
  const pickCategory = (category) => {
    if (category === selectedCategory) return;
    handleBack();
    if (category === 'directOrder') handleSelectDirectOrder();
    else handleSelectInventoryLocation();
  };

  const handleLocationSubmit = async (values) => {
    try {
      setLocationSubmitting(true);
      const res = await inventoryService.createReservationLocation({
        name: values.name,
        description: values.description || undefined,
      });
      const loc = res.location || res;
      message.success(`"${loc.name || values.name}" submitted for approval — it'll show as pending until an admin or inventory manager reviews it.`, 6);
      locationForm.resetFields();
      onClose();
    } catch (error) {
      message.error(error.message || 'Failed to submit location for approval');
    } finally {
      setLocationSubmitting(false);
    }
  };

  const categories = [
    {
      key: 'directOrder',
      icon: <FileTextTwoTone twoToneColor="#2748d9" />,
      tone: 'accent',
      title: 'Change a direct order',
      text: 'Correct details on an approved order, or ask for it to be deleted.',
    },
    {
      key: 'inventoryLocation',
      icon: <EnvironmentTwoTone twoToneColor="#12805c" />,
      tone: 'ok',
      title: 'Add an inventory location',
      text: 'Ask for a new place to book items to, like a venue or a store.',
    },
  ];

  const renderPicker = () => (
    <div className="tk-pick" role="radiogroup" aria-label="What do you need?">
      {categories.map((c) => (
        <button
          key={c.key}
          type="button"
          role="radio"
          aria-checked={selectedCategory === c.key}
          className={`tk-pick__card tone-${c.tone}${selectedCategory === c.key ? ' is-on' : ''}`}
          onClick={() => pickCategory(c.key)}
        >
          <span className="tk-pick__icon" aria-hidden="true">{c.icon}</span>
          <span className="tk-pick__title">{c.title}</span>
          <span className="tk-pick__text">{c.text}</span>
        </button>
      ))}
    </div>
  );

  // Propose Inventory Location Form
  const renderInventoryLocationForm = () => (
    <div className="tk-form">
      <p className="tk-callout">
        <InfoCircleOutlined aria-hidden="true" />
        An admin or inventory manager reviews new locations before they can be booked. You'll be notified once it's approved.
      </p>
      <Form form={locationForm} layout="vertical" onFinish={handleLocationSubmit} requiredMark={false}>
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Name is required' }]}
        >
          <Input placeholder="e.g. Stage B Storage" />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <TextArea rows={2} placeholder="e.g. Storage container behind Stage B" />
        </Form.Item>
        <div className="tk-foot">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={locationSubmitting}>
            Send for approval
          </Button>
        </div>
      </Form>
    </div>
  );

  // Direct Order Form
  const renderDirectOrderForm = () => (
    <div className="tk-form">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
      >
        <Form.Item
          name="doId"
          label="Direct order"
          rules={[{ required: true, message: 'Please select a Direct Order' }]}
          extra="Only fully approved orders you raised or are copied on can be changed."
        >
          <Select
            showSearch
            placeholder="Search and select a direct order"
            loading={loadingDOs}
            onChange={handleDOChange}
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {directOrders.map(order => (
              <Option key={order.doId} value={order.doId}>
                {`${order.doRef ?? ''} - ${order.doSupplier ?? ''} (${order.doTotalCost ?? ''})`}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {selectedDO && (
          <div className="tk-selected">
            <span className="tk-selected__ref num">#{selectedDO.doRef}</span>
            <span className="tk-selected__meta">{selectedDO.doSupplier}</span>
            <span className="tk-selected__cost num">{selectedDO.doTotalCost}</span>
          </div>
        )}

        <Form.Item label="What do you need?">
          <Radio.Group
            className="tk-type"
            value={ticketType}
            onChange={(e) => setTicketType(e.target.value)}
            optionType="button"
          >
            <Radio.Button value="amend"><EditOutlined aria-hidden="true" /> Change details</Radio.Button>
            <Radio.Button value="delete"><DeleteOutlined aria-hidden="true" /> Delete the order</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {ticketType === 'amend' && selectedDO && (
          <>
            <Form.Item
              name="fieldsToAmend"
              label="Fields to change"
              rules={[{ required: true, message: 'Please select at least one field' }]}
            >
              <Select
                mode="multiple"
                placeholder="Pick the fields you want to change"
                onChange={(values) => setSelectedFields(values)}
              >
                {Object.keys(AMENDABLE_FIELDS).map(key => (
                  <Option key={key} value={key}>
                    {AMENDABLE_FIELDS[key].label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {selectedFields.length > 0 && (
              <div className="tk-changes">
                {selectedFields.map(fieldKey => {
                  const field = AMENDABLE_FIELDS[fieldKey];
                  const rawValue = selectedDO[fieldKey];
                  const fieldOptions = SELECT_OPTIONS_BY_FIELD[fieldKey] || [];
                  // For lookup fields the raw value is an id — show its label if we
                  // can resolve it, falling back to the raw value otherwise.
                  const currentValue = field.type === 'select'
                    ? (fieldOptions.find(o => String(o.value) === String(rawValue))?.label ?? rawValue)
                    : rawValue;

                  return (
                    <div key={fieldKey} className="tk-change">
                      <div className="tk-change__head">
                        <span className="tk-change__label">{field.label}</span>
                        <span className="tk-change__current">
                          Now: {currentValue !== undefined && currentValue !== null && currentValue !== '' ? String(currentValue) : '—'}
                        </span>
                      </div>
                      <Form.Item
                        name={`change_${fieldKey}`}
                        rules={[
                          { required: true, message: `Please select/enter new ${field.label}` },
                          ...(fieldKey === 'doTotalCost' ? [totalLimitRule] : []),
                        ]}
                        extra={fieldKey === 'doTotalCost' ? `Up to ${LIMIT_LABEL} (excl. VAT)` : undefined}
                        style={{ marginBottom: 0 }}
                      >
                        {field.type === 'select' ? (
                          <Select
                            showSearch
                            optionFilterProp="label"
                            options={fieldOptions}
                            placeholder={`New ${field.label.toLowerCase()}`}
                            aria-label={`New ${field.label}`}
                          />
                        ) : field.type === 'textarea' ? (
                          <TextArea rows={2} placeholder={`New ${field.label.toLowerCase()}`} aria-label={`New ${field.label}`} />
                        ) : field.type === 'number' ? (
                          <InputNumber style={{ width: '100%' }} placeholder={`New ${field.label.toLowerCase()}`} aria-label={`New ${field.label}`} />
                        ) : field.type === 'date' ? (
                          <Input type="date" aria-label={`New ${field.label}`} />
                        ) : (
                          <Input placeholder={`New ${field.label.toLowerCase()}`} aria-label={`New ${field.label}`} />
                        )}
                      </Form.Item>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {ticketType === 'delete' && (
          <p className="tk-callout tone-bad">
            <InfoCircleOutlined aria-hidden="true" />
            Deleting needs one approval, from {approverNames[0] || 'the first approver'}.
          </p>
        )}

        <Form.Item
          name="reason"
          label="Reason"
          extra="Explain why this change is needed. The approvers see this."
        >
          <TextArea rows={3} placeholder="e.g. Supplier name was entered incorrectly" />
        </Form.Item>

        {submitError && (
          <Alert
            message={submitError.existingTicketId ? `Open ticket #${submitError.existingTicketId} already exists` : 'Could not create ticket'}
            description={submitError.message}
            type="warning"
            showIcon
            closable
            onClose={() => setSubmitError(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        <div className="tk-foot">
          <span className="tk-foot__note">
            {ticketType === 'amend' ? chainText : `${firstApprover} is notified automatically.`}
          </span>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            Send request
          </Button>
        </div>
      </Form>
    </div>
  );

  const renderMyTickets = () => (
    <aside className="tk-mine" aria-labelledby="tk-mine-title">
      <h3 className="tk-mine__title" id="tk-mine-title">Your requests</h3>
      {myTicketsLoading ? (
        <div className="tk-mine__empty"><Spin size="small" /></div>
      ) : myTickets.length === 0 ? (
        <p className="tk-mine__empty">Direct order requests you send appear here, with where they are.</p>
      ) : (
        <ul className="tk-mine__list">
          {myTickets.slice(0, 6).map((t) => {
            const st = ticketStatus(t.status);
            const { approvedCount, totalRequired } = getApprovalStatus(t);
            return (
              <li key={t.ticket_id} className="tk-mine__item">
                <span className="tk-mine__name">
                  #{t.ticket_id} {t.ticket_type === 'delete' ? 'Delete' : 'Change'} order {t.doRef ? `#${t.doRef}` : ''}
                </span>
                <span className="tk-mine__meta">
                  {t.created_at ? `Sent ${dayjs(t.created_at).format('D MMM')}` : ''}
                  {t.status === 'open' ? ` · ${approvedCount} of ${totalRequired} approvals` : ''}
                </span>
                <span className={`tk-status tone-${st.tone}`}>{st.label}</span>
                {t.status === 'rejected' && t.admin_comment && (
                  <span className="tk-mine__comment">“{t.admin_comment}”</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={980}
      destroyOnHidden
      rootClassName="do-scope tk-modal"
    >
      <DOTheme>
        <div className="tk-req">
          <div className="tk-req__main">
            <header className="tk-req__head">
              <h2 className="tk-req__title">Get help</h2>
              <p className="tk-req__sub">Ask for a change the portal can't make by itself. Someone will review it.</p>
            </header>
            {renderPicker()}
            {step === 'select'
              ? <p className="tk-req__hint">Pick what you need to get started.</p>
              : selectedCategory === 'inventoryLocation'
                ? renderInventoryLocationForm()
                : renderDirectOrderForm()}
          </div>
          {renderMyTickets()}
        </div>
      </DOTheme>
    </Modal>
  );
};

export default CreateTicketModal;
